<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
    NextButton,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList(true, { includeAgentBots: true });
    return {
      agentsList,
    };
  },
  data() {
    return {
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          icon: 'i-woot-priority-empty',
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          icon: 'i-woot-priority-urgent',
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          icon: 'i-woot-priority-high',
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          icon: 'i-woot-priority-medium',
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          icon: 'i-woot-priority-low',
        },
      ],
      // Kanban
      kanbanBoards: [],
      kanbanMemberships: [],
      showKanbanDropdown: false,
      kanbanLoading: false,
    };
  },
  computed: {
    availableKanbanBoards() {
      const memberBoardIds = this.kanbanMemberships.map(m => m.boardId);
      // Se muestran todos los tableros (compartidos y personales) donde la conversacion
      // aun no este agregada, incluso si el tablero todavia no tiene columnas creadas.
      return this.kanbanBoards.filter(b => !memberBoardIds.includes(b.id));
    },
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
    }),
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    assignedAgent: {
      get() {
        const assignee = this.currentChat.meta.assignee;
        return (
          assignee && {
            ...assignee,
            assignee_type: this.currentChat.meta.assignee_type || 'User',
          }
        );
      },
      set(agent) {
        const agentId = agent ? agent.id : null;
        const assigneeType = agent ? agent.assignee_type || 'User' : null;
        this.$store.dispatch('setCurrentChatAssignee', {
          conversationId: this.currentChat.id,
          assignee: agent,
          assigneeType,
        });
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
            assigneeType,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem.id;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (
        this.assignedAgent.id !== this.currentUser.id ||
        (this.assignedAgent.assignee_type || 'User') !== 'User'
      ) {
        return true;
      }
      return false;
    },
  },
  mounted() {
    this.checkKanbanStatus();
  },
  watch: {
    conversationId() {
      this.showKanbanDropdown = false;
      this.kanbanMemberships = [];
      this.checkKanbanStatus();
    },
  },
  methods: {
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (
        this.assignedAgent?.id === selectedItem.id &&
        (this.assignedAgent?.assignee_type || 'User') ===
          (selectedItem.assignee_type || 'User')
      ) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },

    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority
        ? this.priorityOptions[0]
        : selectedPriorityItem;
    },

    // --- Kanban methods ---
    getToken() {
      try {
        const app = document.querySelector('#app').__vue_app__;
        const store = app.config.globalProperties.$store;
        return store.state.auth.currentUser?.access_token || null;
      } catch (e) {
        return null;
      }
    },

    async checkKanbanStatus() {
      const token = this.getToken();
      if (!token) return;
      const accountId = this.$route.params.accountId;
      try {
        const res = await fetch(`/api/v1/accounts/${accountId}/kanban_boards`, {
          headers: { 'api_access_token': token },
        });
        const boardsIndex = await res.json();
        if (!Array.isArray(boardsIndex) || !boardsIndex.length) return;

        const boards = [];
        const memberships = [];
        for (const b of boardsIndex) {
          const boardRes = await fetch(
            `/api/v1/accounts/${accountId}/kanban_boards/${b.id}`,
            { headers: { 'api_access_token': token } }
          );
          const board = await boardRes.json();
          const columns = (board.kanban_columns || [])
            .slice()
            .sort((x, y) => x.position - y.position);
          boards.push({ id: b.id, name: b.name, user_id: b.user_id, columns });
          for (const col of columns) {
            const found = (col.kanban_cards || []).find(
              c => c.conversation_id === Number(this.conversationId)
            );
            if (found) {
              memberships.push({
                boardId: b.id,
                boardName: b.name,
                columnId: col.id,
                columnName: col.name,
                columnColor: col.color,
                cardId: found.id,
              });
              break;
            }
          }
        }
        this.kanbanBoards = boards;
        this.kanbanMemberships = memberships;
      } catch (e) {
        // silently fail
      }
    },

    toggleKanbanDropdown() {
      this.showKanbanDropdown = !this.showKanbanDropdown;
    },

    async addToKanban(board, column) {
      const token = this.getToken();
      if (!token) return;
      this.kanbanLoading = true;
      this.showKanbanDropdown = false;
      const accountId = this.$route.params.accountId;
      try {
        const res = await fetch(
          `/api/v1/accounts/${accountId}/kanban_boards/${board.id}/kanban_columns/${column.id}/kanban_cards`,
          {
            method: 'POST',
            headers: {
              'api_access_token': token,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              kanban_card: {
                conversation_id: Number(this.conversationId),
              },
            }),
          }
        );
        if (!res.ok) {
          const data = await res.json();
          useAlert((data.errors || []).join(', ') || 'Error al agregar al Kanban');
        } else {
          useAlert(`Agregada a "${column.name}" del tablero "${board.name}"`);
          await this.checkKanbanStatus();
        }
      } catch (e) {
        useAlert('Error al agregar al Kanban');
      } finally {
        this.kanbanLoading = false;
      }
    },

    async removeFromKanban(membership) {
      const token = this.getToken();
      if (!token) return;
      const accountId = this.$route.params.accountId;
      try {
        await fetch(
          `/api/v1/accounts/${accountId}/kanban_boards/${membership.boardId}/kanban_columns/${membership.columnId}/kanban_cards/${membership.cardId}`,
          {
            method: 'DELETE',
            headers: { 'api_access_token': token },
          }
        );
        await this.checkKanbanStatus();
        useAlert(`Eliminada del tablero "${membership.boardName}"`);
      } catch (e) {
        useAlert('Error al eliminar del Kanban');
      }
    },
  },
};
</script>

<template>
  <div>
    <div>
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template #button>
          <NextButton
            v-if="showSelfAssign"
            link
            xs
            icon="i-lucide-arrow-right"
            class="!gap-1"
            :label="$t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
            @click="onSelfAssign"
          />
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        :options="agentsList"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @select="onClickAssignAgent"
      />
    </div>
    <div>
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <MultiselectDropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        show-emoji-icon
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
        "
        @select="onClickAssignTeam"
      />
    </div>
    <div>
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        @select="onClickAssignPriority"
      />
    </div>
    <ContactDetailsItem
      compact
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
    />
    <ConversationLabels :conversation-id="conversationId" />
    <!-- Kanban Section -->
    <div class="kanban-section">
      <ContactDetailsItem compact title="Kanban" />

      <div class="kanban-action">
        <!-- Tableros donde ya está -->
        <div
          v-for="m in kanbanMemberships"
          :key="m.boardId"
          class="kanban-added-state"
        >
          <span class="kanban-added-badge" :title="`Tablero: ${m.boardName}`">
            <span class="kanban-col-dot" :style="{ background: m.columnColor }" />
            <span class="kanban-added-board">{{ m.boardName }}</span>
            <span class="kanban-added-sep">›</span>
            {{ m.columnName }}
          </span>
          <button class="kanban-remove-btn" @click="removeFromKanban(m)">
            Quitar
          </button>
        </div>

        <!-- Boton agregar (si queda algun tablero disponible) -->
        <div v-if="availableKanbanBoards.length" class="kanban-add-wrap">
          <button
            class="kanban-add-btn"
            :disabled="kanbanLoading"
            @click="toggleKanbanDropdown"
          >
            <span v-if="kanbanLoading">Agregando…</span>
            <span v-else>+ Agregar al Kanban</span>
          </button>

          <!-- Dropdown: tablero > columnas -->
          <div v-if="showKanbanDropdown" class="kanban-dropdown">
            <template v-for="board in availableKanbanBoards" :key="board.id">
              <div class="kanban-dropdown-board">
                {{ board.name }}
                <span
                  class="kanban-board-badge"
                  :class="board.user_id ? 'kanban-board-badge--personal' : 'kanban-board-badge--shared'"
                >{{ board.user_id ? 'Personal' : 'Compartido' }}</span>
              </div>
              <div
                v-for="col in board.columns"
                :key="`${board.id}-${col.id}`"
                class="kanban-dropdown-item"
                @click="addToKanban(board, col)"
              >
                <span
                  class="kanban-col-dot"
                  :style="{ background: col.color }"
                />
                {{ col.name }}
              </div>
              <div v-if="!board.columns.length" class="kanban-dropdown-empty">
                Sin columnas — crea una en el tablero
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.kanban-section {
  margin-top: 0.5rem;
}

.kanban-action {
  padding: 0 0.5rem 0.5rem;
  position: relative;
}

/* Botón agregar */
.kanban-add-btn {
  width: 100%;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px dashed var(--color-border, #d1d5db);
  background: transparent;
  color: var(--color-woot, #6366f1);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s, border-color 0.15s;
}
.kanban-add-btn:hover:not(:disabled) {
  background: var(--color-woot-25, rgba(99,102,241,0.08));
  border-color: var(--color-woot, #6366f1);
  border-style: solid;
}
.kanban-add-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Dropdown */
.kanban-dropdown {
  position: absolute;
  left: 0.5rem;
  right: 0.5rem;
  top: calc(100% - 0.25rem);
  background: var(--white, #ffffff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  z-index: 100;
  overflow: hidden;
}

.kanban-dropdown-board {
  padding: 6px 12px 2px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 6px;
}
.kanban-board-badge {
  font-size: 0.58rem;
  font-weight: 700;
  text-transform: none;
  letter-spacing: normal;
  padding: 1px 6px;
  border-radius: 8px;
}
.kanban-board-badge--shared { background: rgba(99,102,241,0.14); color: #6366f1; }
.kanban-board-badge--personal { background: rgba(245,158,11,0.16); color: #b45309; }
body.dark .kanban-board-badge--shared { background: rgba(99,102,241,0.22); color: #a5b4fc; }
body.dark .kanban-board-badge--personal { background: rgba(245,158,11,0.22); color: #fcd34d; }
.kanban-dropdown-empty {
  padding: 2px 12px 8px;
  font-size: 0.72rem;
  font-style: italic;
  color: #9ca3af;
}
.kanban-added-board { font-weight: 700; }
.kanban-added-sep { opacity: 0.5; margin: 0 2px; }
.kanban-added-state + .kanban-added-state { margin-top: 4px; }
.kanban-added-state + .kanban-add-wrap { margin-top: 6px; }
.kanban-dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 12px;
  font-size: 0.82rem;
  cursor: pointer;
  color: var(--color-body, #374151);
  transition: background 0.1s;
}
.kanban-dropdown-item:hover {
  background: var(--color-background-light, #f3f4f6);
}

.kanban-col-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Estado "ya agregado" */
.kanban-added-state {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.kanban-added-badge {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: var(--color-body, #374151);
  background: var(--color-background-light, #f3f4f6);
  border-radius: 6px;
  padding: 4px 10px;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kanban-icon {
  font-size: 0.65rem;
  opacity: 0.5;
}

.kanban-remove-btn {
  font-size: 0.75rem;
  color: var(--color-alert, #ef4444);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}
.kanban-remove-btn:hover {
  background: var(--color-alert-light, #fee2e2);
}

/* Dark mode */
body.dark .kanban-dropdown {
  background: var(--color-background-dark, #1f2937);
  border-color: var(--color-border-dark, #374151);
}
body.dark .kanban-dropdown-item {
  color: var(--color-heading, #f9fafb);
}
body.dark .kanban-dropdown-item:hover {
  background: var(--color-background-medium, #374151);
}
body.dark .kanban-added-badge {
  background: var(--color-background-medium, #374151);
  color: var(--color-heading, #f9fafb);
}
body.dark .kanban-add-btn {
  border-color: var(--color-border-dark, #4b5563);
  color: var(--color-woot-light, #818cf8);
}
</style>
